import { ApiClient } from '@/libs/device-auth/services/authService';
import { sessionService } from './sessionService';

export class FetchApiClient implements ApiClient {
  private async request<T>(
    method: string,
    url: string,
    options: {
      body?: Record<string, unknown>;
      params?: Record<string, string | number | boolean>;
      headers?: Record<string, string>;
      signal?: AbortSignal;
    } = {},
  ): Promise<T> {
    const { body, params, headers = {}, signal } = options;

    const finalUrl = new URL(url);

    // Append query params for all methods
    Object.entries(params || {}).forEach(([key, value]) => {
      finalUrl.searchParams.append(key, String(value));
    });

    // Determine request configuration based on HTTP method
    const isBodyMethod = ['POST', 'PUT', 'PATCH'].includes(
      method.toUpperCase(),
    );
    let serializedBody: BodyInit | undefined;
    const requestHeaders = { ...headers };

    // Only serialize body for methods that support it
    if (isBodyMethod && body) {
      const contentType = headers['Content-Type'] || 'application/json';

      if (contentType === 'application/x-www-form-urlencoded') {
        // Convert Record<string, unknown> to Record<string, string> for URLSearchParams
        const formData = Object.entries(body).reduce((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {} as Record<string, string>);
        serializedBody = new URLSearchParams(formData).toString();
      } else {
        serializedBody = JSON.stringify(body);
      }

      // Set Content-Type header for body methods
      requestHeaders['Content-Type'] = contentType;
    }

    const response = await fetch(finalUrl.toString(), {
      method,
      headers: requestHeaders,
      ...(isBodyMethod && serializedBody && { body: serializedBody }),
      signal,
    });

    if (response.status === 401) {
      await sessionService.clearSession();
      return response.json();
    }

    // Let consumers handle HTTP status for non-401 responses
    return response.json();
  }

  async get<T>(
    url: string,
    params?: Record<string, string | number | boolean>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>('GET', url, { params, headers, signal });
  }

  async post<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>('POST', url, { body: data, headers, signal });
  }

  async put<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>('PUT', url, { body: data, headers, signal });
  }

  async delete<T>(
    url: string,
    params?: Record<string, string | number | boolean>,
    headers?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.request<T>('DELETE', url, { params, headers, signal });
  }
}
